import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SocialCommentDetailDto,
  SocialCommentHandlingStatus,
  SocialCommentThreadResponse,
} from "@multi-chat/shared";
import {
  getSocialCommentThread,
  markSocialCommentRead,
  sendSocialCommentPrivateReply,
  sendSocialCommentPublicReply,
  updateSocialCommentHandlingStatus,
} from "../../../api/social-comments-api.js";
import { useInboxRealtime } from "../InboxRealtimeProvider.js";

export type CommentThreadView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: SocialCommentThreadResponse };

export function useCommentThread(
  organizationId: number,
  commentId: string | null,
  unreachableMessage: string,
  enabled = true,
): {
  threadView: CommentThreadView;
  scope: "thread" | "all_post_comments";
  setScope: (scope: "thread" | "all_post_comments") => void;
  refresh: () => void;
  markHandled: () => Promise<boolean>;
  reopen: () => Promise<boolean>;
  sendPublicReply: (content: string, options?: { markHandled?: boolean }) => Promise<boolean>;
  sendPrivateReply: (content: string, options?: { markHandled?: boolean }) => Promise<boolean>;
  workflowPending: boolean;
} {
  const { subscribeSocialCommentChanged, subscribeReconnect } = useInboxRealtime();
  const [threadView, setThreadView] = useState<CommentThreadView>({ kind: "idle" });
  const [scope, setScope] = useState<"thread" | "all_post_comments">("thread");
  const [workflowPending, setWorkflowPending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const orgRef = useRef(organizationId);
  const commentRef = useRef(commentId);
  const enabledRef = useRef(enabled);
  const readMarkedRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const orgId = organizationId;
    const id = commentId;
    if (!enabledRef.current || id == null) {
      setThreadView({ kind: "idle" });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setThreadView((current) => (current.kind === "ready" ? current : { kind: "loading" }));
    try {
      const data = await getSocialCommentThread(orgId, id, {
        scope,
        signal: controller.signal,
      });
      if (controller.signal.aborted || orgRef.current !== orgId || commentRef.current !== id) {
        return;
      }
      setThreadView({ kind: "ready", data });
      if (!data.selectedComment.isRead && readMarkedRef.current !== id) {
        readMarkedRef.current = id;
        void markSocialCommentRead(orgId, id).catch(() => {
          /* non-blocking */
        });
      }
    } catch {
      if (controller.signal.aborted || orgRef.current !== orgId || commentRef.current !== id) {
        return;
      }
      setThreadView({ kind: "error", message: unreachableMessage });
    }
  }, [commentId, organizationId, scope, unreachableMessage]);

  useEffect(() => {
    enabledRef.current = enabled;
    orgRef.current = organizationId;
    commentRef.current = commentId;
    if (!enabled || commentId == null) {
      readMarkedRef.current = null;
      setThreadView({ kind: "idle" });
      abortRef.current?.abort();
      return;
    }
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [commentId, enabled, load, organizationId]);

  useEffect(() => {
    if (!enabled || commentId == null) return;
    const unsubEvent = subscribeSocialCommentChanged((event) => {
      if (event.socialCommentId === commentId || threadView.kind === "ready") {
        void load();
      }
    });
    const unsubReconnect = subscribeReconnect(() => {
      void load();
    });
    return () => {
      unsubEvent();
      unsubReconnect();
    };
  }, [commentId, enabled, load, subscribeReconnect, subscribeSocialCommentChanged, threadView.kind]);

  const updateStatus = useCallback(
    async (handlingStatus: SocialCommentHandlingStatus): Promise<boolean> => {
      if (commentId == null) return false;
      setWorkflowPending(true);
      try {
        const updated = await updateSocialCommentHandlingStatus(
          organizationId,
          commentId,
          handlingStatus,
        );
        setThreadView((current) => {
          if (current.kind !== "ready") return current;
          return {
            kind: "ready",
            data: {
              ...current.data,
              selectedComment: mergeComment(current.data.selectedComment, updated),
            },
          };
        });
        return true;
      } catch {
        return false;
      } finally {
        setWorkflowPending(false);
      }
    },
    [commentId, organizationId],
  );

  const sendPublicReply = useCallback(
    async (content: string, options?: { markHandled?: boolean }): Promise<boolean> => {
      if (commentId == null) return false;
      setWorkflowPending(true);
      try {
        await sendSocialCommentPublicReply(organizationId, commentId, {
          content,
          clientIdempotencyKey: crypto.randomUUID(),
          markHandled: options?.markHandled,
        });
        await load();
        return true;
      } catch {
        return false;
      } finally {
        setWorkflowPending(false);
      }
    },
    [commentId, load, organizationId],
  );

  const sendPrivateReply = useCallback(
    async (content: string, options?: { markHandled?: boolean }): Promise<boolean> => {
      if (commentId == null) return false;
      setWorkflowPending(true);
      try {
        await sendSocialCommentPrivateReply(organizationId, commentId, {
          content,
          clientIdempotencyKey: crypto.randomUUID(),
          markHandled: options?.markHandled,
        });
        await load();
        return true;
      } catch {
        return false;
      } finally {
        setWorkflowPending(false);
      }
    },
    [commentId, load, organizationId],
  );

  return {
    threadView,
    scope,
    setScope,
    refresh: () => {
      void load();
    },
    markHandled: () => updateStatus("HANDLED"),
    reopen: () => updateStatus("OPEN"),
    sendPublicReply,
    sendPrivateReply,
    workflowPending,
  };
}

function mergeComment(
  current: SocialCommentDetailDto,
  updated: SocialCommentDetailDto,
): SocialCommentDetailDto {
  return { ...current, ...updated };
}
